import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { uploadFileConfig } from '@/pages/TrackingPage/components/LookAndFeel/components/ChooseTheme/config';
import { isUrlValid } from '@/helpers';

export interface IAssetMediaItem {
  file?: File | null;
  index: number;
  url?: string;
  value?: string;
  baseUrl?: string;
  shop?: string;
  path?: string;
  id?: number;
  previewUrl?: string;
}

export interface IAssetMedia {
  asset1?: IAssetMediaItem;
  asset2?: IAssetMediaItem;
  asset3?: IAssetMediaItem;
  asset4?: IAssetMediaItem;
  asset5?: IAssetMediaItem;
}

// Define the initial state using that type
export const initAssetData: IAssetMedia = {
  asset1: {
    index: 1,
    value: '',
    url: '',
    file: null,
  },
  asset2: {
    index: 2,
    value: '',
    url: '',
    file: null,
  },
  asset3: {
    index: 3,
    value: '',
    url: '',
    file: null,
  },
  asset4: {
    index: 4,
    value: '',
    url: '',
    file: null,
  },
  asset5: {
    index: 5,
    value: '',
    url: '',
    file: null,
  },
};

interface IInit {
  data?: IAssetMedia;
  oldData?: IAssetMedia;
  validation?: string[];
}

const initialState: IInit = {
  data: initAssetData,
  oldData: initAssetData,
  validation: [],
};

export const trackingPageMediaSlice = createSlice({
  name: 'trackingPageMedia',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    handleUpdateMedia: (state, action: PayloadAction<IInit>) => {
      const { data, oldData, validation } = action.payload;
      if (data) {
        state.data = data;
      }
      if (oldData) {
        state.oldData = oldData;
      }
      if (validation) state.validation = validation;
    },
  },
});

// Other code such as selectors can use the imported `RootState` type
export const trackingPageMediaSelector = createSelector(
  (state: RootState) => state.trackingPageMedia,
  (state) => state,
);

export const isEnableSaveMediaSelector = createSelector(
  (state: RootState) => state.trackingPageMedia,
  (state) => {
    const temp = [...uploadFileConfig.trackingForm, ...uploadFileConfig.trackingResult];
    return !(
      temp.every((item) => {
        const asset = state.data?.[item.key as keyof typeof state.data];
        const oldAsset = state.oldData?.[item.key as keyof typeof state.oldData];
        return asset?.value === oldAsset?.value && asset?.file === oldAsset?.file;
      }) ||
      temp?.some((item) => {
        const asset = state.data?.[item.key as keyof typeof state.data];

        return asset?.value ? !isUrlValid(asset?.value) : false;
      })
    );
  },
);

export default trackingPageMediaSlice;
